import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Spin } from "antd";
import { Check, Link2 } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProductTourHeroModal from "./ProductTourHeroModal";
import {
    clearPostOnboardingSetupPending,
    isPostOnboardingSetupPending,
} from "./postOnboardingSetupStorage";
import { useUser } from "../../contexts/UserContext/UserContext";
import { useCarContext } from "../../contexts/CarContext";
import { useCategoriesContext } from "../../contexts/CategoriesContext";
import { useToast } from "../../contexts/ToastContext";
import VinVerifyStep from "../../features/cars/components/addCar/VinVerifyStep";
import AddCarModal from "../../features/cars/components/modals/AddCarModal";
import stripeImage from "../../assets/icons/stripe.svg";
import {
    applyStripeOnboardingReturn,
    connectStripeAccount,
    isStripeConnected,
} from "../../features/payment/stripeOnboarding";

const SETUP_PHASE = {
    CAR: "car",
    STRIPE: "stripe",
};

const PostOnboardingSetupFlow = ({ enabled, onComplete, onReadyForTour }) => {
    const { t } = useTranslation("productTour");
    const { t: tSettings } = useTranslation("settings");
    const { myCompanyData, fetchCompanyData } = useUser();
    const { totalItemCount, fetchCars } = useCarContext();
    const { verifyCarCreationPrerequisites } = useCategoriesContext();
    const toast = useToast();
    const history = useHistory();
    const location = useLocation();

    const [initializing, setInitializing] = useState(true);
    const [phase, setPhase] = useState(null);
    const [stepPlan, setStepPlan] = useState(null);
    const [addCarModalOpen, setAddCarModalOpen] = useState(false);
    const [decodedVehiclePrefill, setDecodedVehiclePrefill] = useState(null);
    const [manualVinPrefill, setManualVinPrefill] = useState("");
    const [connectingStripe, setConnectingStripe] = useState(false);
    const [carPrerequisitesOk, setCarPrerequisitesOk] = useState(true);
    const processedStripeReturn = useRef(false);
    const flowFinishedRef = useRef(false);
    const bootstrappedRef = useRef(false);
    const bootstrapCancelledRef = useRef(false);

    const hasCars = totalItemCount > 0;
    const stripeConnected = isStripeConnected(myCompanyData);

    const finishFlow = useCallback(() => {
        if (flowFinishedRef.current) {
            return;
        }
        flowFinishedRef.current = true;
        clearPostOnboardingSetupPending();
        setPhase(null);
        onComplete();
        onReadyForTour();
    }, [onComplete, onReadyForTour]);

    const goToStripeOrFinish = useCallback(() => {
        if (!isStripeConnected(myCompanyData) && stepPlan?.includeStripe) {
            setPhase(SETUP_PHASE.STRIPE);
            return;
        }
        finishFlow();
    }, [finishFlow, myCompanyData, stepPlan?.includeStripe]);

    const skipCurrentStep = useCallback(() => {
        if (phase === SETUP_PHASE.CAR) {
            goToStripeOrFinish();
            return;
        }
        finishFlow();
    }, [finishFlow, goToStripeOrFinish, phase]);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        document.body.classList.add("post-onboarding-setup-open");
        return () => document.body.classList.remove("post-onboarding-setup-open");
    }, [enabled]);

    useEffect(() => {
        if (!enabled) {
            flowFinishedRef.current = false;
            bootstrappedRef.current = false;
            bootstrapCancelledRef.current = true;
            return undefined;
        }

        if (bootstrappedRef.current) {
            return undefined;
        }

        bootstrappedRef.current = true;
        bootstrapCancelledRef.current = false;

        (async () => {
            setInitializing(true);
            try {
                await Promise.all([fetchCars(true), fetchCompanyData(), verifyCarCreationPrerequisites()]);
            } finally {
                if (!bootstrapCancelledRef.current) {
                    setInitializing(false);
                }
            }
        })();

        return () => {
            bootstrapCancelledRef.current = true;
        };
    }, [enabled, fetchCars, fetchCompanyData, verifyCarCreationPrerequisites]);

    useEffect(() => {
        if (!enabled || initializing || stepPlan || !myCompanyData) {
            return;
        }

        setStepPlan({
            includeCar: totalItemCount === 0,
            includeStripe: !isStripeConnected(myCompanyData),
        });
    }, [enabled, initializing, myCompanyData, stepPlan, totalItemCount]);

    useEffect(() => {
        if (!enabled || initializing || !stepPlan || !myCompanyData || phase) {
            return;
        }

        if (stepPlan.includeCar) {
            setPhase(SETUP_PHASE.CAR);
            return;
        }

        if (stepPlan.includeStripe) {
            setPhase(SETUP_PHASE.STRIPE);
            return;
        }

        finishFlow();
    }, [enabled, finishFlow, initializing, myCompanyData, phase, stepPlan]);

    useEffect(() => {
        if (!enabled || phase !== SETUP_PHASE.CAR) {
            return;
        }

        verifyCarCreationPrerequisites().then(({ hasLocations }) => {
            setCarPrerequisitesOk(hasLocations);
        });
    }, [enabled, phase, verifyCarCreationPrerequisites]);

    useEffect(() => {
        if (phase === SETUP_PHASE.CAR && hasCars) {
            goToStripeOrFinish();
        }
    }, [goToStripeOrFinish, hasCars, phase]);

    const clearStripeReturnParams = useCallback(() => {
        history.replace({ pathname: location.pathname, search: "" });
    }, [history, location.pathname]);

    useEffect(() => {
        if (!enabled || !myCompanyData || processedStripeReturn.current) {
            return;
        }

        const query = new URLSearchParams(location.search);
        const stripeFailed = query.get("stripe-onboard-connection-failed");
        const stripeSuccess = query.get("stripe-onboard-connection-success");

        if (!stripeFailed && !stripeSuccess) {
            return;
        }

        processedStripeReturn.current = true;

        if (stripeFailed) {
            toast.error({
                message: tSettings("integrations.stripeOnboardingError"),
            });
            clearStripeReturnParams();
            if (isPostOnboardingSetupPending()) {
                setPhase(SETUP_PHASE.STRIPE);
            }
            return;
        }

        (async () => {
            try {
                await applyStripeOnboardingReturn(query, fetchCompanyData);
                toast.success({
                    message: tSettings("integrations.stripeConnectedExisting"),
                });
            } catch {
                toast.error({
                    message: tSettings("integrations.stripeOnboardingError"),
                });
            } finally {
                clearStripeReturnParams();
                setPhase(SETUP_PHASE.STRIPE);
            }
        })();
    }, [clearStripeReturnParams, enabled, fetchCompanyData, location.search, tSettings, toast]);

    useEffect(() => {
        if (phase === SETUP_PHASE.STRIPE && stripeConnected) {
            finishFlow();
        }
    }, [finishFlow, phase, stripeConnected]);

    const handleModalClose = () => {
        setAddCarModalOpen(false);
        setDecodedVehiclePrefill(null);
        setManualVinPrefill("");
    };

    const openManualModal = (vinValue = "") => {
        setDecodedVehiclePrefill(null);
        setManualVinPrefill(vinValue || "");
        setAddCarModalOpen(true);
    };

    const handleContinueFromVin = (decoded) => {
        setDecodedVehiclePrefill(decoded);
        setAddCarModalOpen(true);
    };

    const connectStripe = useCallback(async () => {
        try {
            setConnectingStripe(true);
            const returnPath = "/?softcar_post_onboarding_setup=1";
            const result = await connectStripeAccount({
                originUrl: window.location.origin,
                returnPath,
            });

            if (result.kind === "existing") {
                await fetchCompanyData();
                toast.success({
                    message: tSettings("integrations.stripeConnectedExisting"),
                });
                return;
            }

            if (result.kind === "redirect" && result.url) {
                window.location.href = result.url;
            }
        } catch {
            toast.error({
                message: tSettings("integrations.connectionFailed"),
                description: tSettings("integrations.somethingWentWrong"),
            });
        } finally {
            setConnectingStripe(false);
        }
    }, [fetchCompanyData, tSettings, toast]);

    const carModalOpen = enabled && phase === SETUP_PHASE.CAR && !initializing;
    const stripeModalOpen = enabled && phase === SETUP_PHASE.STRIPE && !initializing;

    if (!enabled) {
        return null;
    }

    return (
        <>
            <ProductTourHeroModal
                open={carModalOpen}
                stepId="carsAdd"
                kicker={t("guidedTour")}
                title={t("postOnboardingSetup.addCarTitle")}
                description={t("postOnboardingSetup.addCarDescription")}
                closable={false}
                maskClosable={false}
                showSkip={false}
                showProgress={false}
                showPrimary
                primaryLabel={t("postOnboardingSetup.continue")}
                onPrimary={goToStripeOrFinish}
                primaryDisabled={!hasCars}>
                {!carPrerequisitesOk ? (
                    <p className="text-sm text-amber-700">{t("postOnboardingSetup.addCarNoLocations")}</p>
                ) : (
                    <>
                        <VinVerifyStep
                            translationNamespace="onboarding"
                            translationKeyPrefix="steps.addCar"
                            variant="onboarding"
                            onContinue={handleContinueFromVin}
                            onManualEntry={openManualModal}
                        />
                        {!hasCars ? (
                            <button
                                type="button"
                                onClick={skipCurrentStep}
                                className="mt-4 w-full text-center text-sm font-medium text-[#5d5dff] hover:text-[#4b4bd9]">
                                {t("postOnboardingSetup.addCarLater")}
                            </button>
                        ) : null}
                    </>
                )}
            </ProductTourHeroModal>

            <ProductTourHeroModal
                open={stripeModalOpen}
                stepId="payments"
                kicker={t("guidedTour")}
                title={t("postOnboardingSetup.stripeTitle")}
                description={t("postOnboardingSetup.stripeDescription")}
                closable={false}
                maskClosable={false}
                showSkip={false}
                showProgress={false}
                showPrimary
                primaryLabel={
                    stripeConnected ? t("letsGo") : t("postOnboardingSetup.continueToTour")
                }
                onPrimary={finishFlow}
                primaryDisabled={false}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                    <div className="flex h-16 w-36 shrink-0 items-center justify-start sm:h-20 sm:w-44">
                        <img
                            src={stripeImage}
                            alt="Stripe"
                            className="max-h-full max-w-full object-contain object-left"
                        />
                    </div>
                    <p className="min-w-0 flex-1 text-sm leading-relaxed text-gray-600">
                        {tSettings("integrations.stripeDescription")}
                    </p>
                    {stripeConnected ? (
                        <button
                            type="button"
                            disabled
                            className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg bg-[#5d5dff] px-4 text-sm font-medium text-white">
                            <span className="flex h-4 w-4 items-center justify-center rounded-full border border-white">
                                <Check size={10} strokeWidth={3} />
                            </span>
                            {t("postOnboardingSetup.connected")}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={connectStripe}
                            disabled={connectingStripe}
                            className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border border-[#5d5dff] bg-white px-4 text-sm font-medium text-[#5d5dff] hover:bg-[#5d5dff]/5 disabled:opacity-60">
                            {connectingStripe ? <Spin size="small" /> : <Link2 size={16} strokeWidth={2} aria-hidden />}
                            {t("postOnboardingSetup.connect")}
                        </button>
                    )}
                </div>
            </ProductTourHeroModal>

            <AddCarModal
                open={addCarModalOpen}
                onClose={handleModalClose}
                refetchCars={() => fetchCars(true)}
                decodedVehiclePrefill={decodedVehiclePrefill}
                entryFlow="manual"
                manualVinPrefill={manualVinPrefill}
            />
        </>
    );
};

PostOnboardingSetupFlow.propTypes = {
    enabled: PropTypes.bool.isRequired,
    onComplete: PropTypes.func.isRequired,
    onReadyForTour: PropTypes.func.isRequired,
};

export default PostOnboardingSetupFlow;
